(() => {
  const COMPONENT_NAME = 'dressfit-store-switcher';

  if (customElements.get(COMPONENT_NAME)) return;

  class DressfitStoreSwitcher extends HTMLElement {
    constructor() {
      super();

      this.trigger = null;
      this.panel = null;
      this.backdrop = null;
      this.closeButton = null;
      this.form = null;
      this.countryInput = null;
      this.languageInput = null;
      this.options = [];
      this.loading = null;
      this.lastFocusedElement = null;
      this.closeTimer = null;

      this.handleTriggerClick =
        this.handleTriggerClick.bind(this);

      this.handleBackdropClick =
        this.handleBackdropClick.bind(this);

      this.handleCloseClick =
        this.handleCloseClick.bind(this);

      this.handleDocumentKeydown =
        this.handleDocumentKeydown.bind(this);

      this.handleOptionClick =
        this.handleOptionClick.bind(this);
    }

    connectedCallback() {
      if (this.dataset.initialized === 'true') return;

      this.dataset.initialized = 'true';

      this.trigger =
        this.querySelector('[data-df-store-trigger]');

      this.panel =
        this.querySelector('[data-df-store-panel]');

      this.backdrop =
        this.querySelector('[data-df-store-backdrop]');

      this.closeButton =
        this.querySelector('[data-df-store-close]');

      this.form =
        this.querySelector('form');

      this.countryInput =
        this.querySelector('[data-df-country-input]');

      this.languageInput =
        this.querySelector('[data-df-language-input]');

      this.options = Array.from(
        this.querySelectorAll('[data-df-store-option]')
      );

      this.loading =
        this.querySelector('[data-df-store-loading]');

      if (
        !this.trigger ||
        !this.panel ||
        !this.backdrop ||
        !this.form ||
        !this.countryInput ||
        !this.languageInput
      ) {
        return;
      }

      this.trigger.addEventListener(
        'click',
        this.handleTriggerClick
      );

      this.backdrop.addEventListener(
        'click',
        this.handleBackdropClick
      );

      if (this.closeButton) {
        this.closeButton.addEventListener(
          'click',
          this.handleCloseClick
        );
      }

      this.options.forEach((option) => {
        option.addEventListener(
          'click',
          this.handleOptionClick
        );
      });
    }

    disconnectedCallback() {
      this.trigger?.removeEventListener(
        'click',
        this.handleTriggerClick
      );

      this.backdrop?.removeEventListener(
        'click',
        this.handleBackdropClick
      );

      this.closeButton?.removeEventListener(
        'click',
        this.handleCloseClick
      );

      this.options.forEach((option) => {
        option.removeEventListener(
          'click',
          this.handleOptionClick
        );
      });

      document.removeEventListener(
        'keydown',
        this.handleDocumentKeydown
      );
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    handleTriggerClick() {
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
      }
    }

    handleBackdropClick() {
      this.close();
    }

    handleCloseClick() {
      this.close();
    }

    handleDocumentKeydown(event) {
      if (!this.isOpen) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key === 'Tab') {
        this.trapFocus(event);
      }
    }

    handleOptionClick(event) {
      const option = event.currentTarget;

      if (
        option.getAttribute('aria-current') === 'true' ||
        option.getAttribute('aria-disabled') === 'true'
      ) {
        return;
      }

      const languageCode =
        option.dataset.languageCode;

      const countryCode =
        option.dataset.countryCode;

      if (!languageCode || !countryCode) return;

      this.languageInput.value = languageCode;
      this.countryInput.value = countryCode;

      this.classList.add('is-loading');

      if (this.loading) {
        this.loading.hidden = false;
      }

      this.options.forEach((item) => {
        item.setAttribute('aria-disabled', 'true');
      });

      if (
        typeof this.form.requestSubmit === 'function'
      ) {
        this.form.requestSubmit();
      } else {
        this.form.submit();
      }
    }

    open() {
      window.clearTimeout(this.closeTimer);

      this.lastFocusedElement =
        document.activeElement;

      document
        .querySelectorAll(`${COMPONENT_NAME}.is-open`)
        .forEach((component) => {
          if (
            component !== this &&
            typeof component.close === 'function'
          ) {
            component.close(false);
          }
        });

      this.panel.hidden = false;
      this.backdrop.hidden = false;

      this.classList.add('is-open');

      this.trigger.setAttribute(
        'aria-expanded',
        'true'
      );

      document.body.classList.add(
        'df-store-switcher-open'
      );

      document.addEventListener(
        'keydown',
        this.handleDocumentKeydown
      );

      window.requestAnimationFrame(() => {
        this.panel.classList.add('is-visible');

        if (this.closeButton) {
          this.closeButton.focus();
        } else {
          this.panel.focus();
        }
      });
    }

    close(restoreFocus = true) {
      if (!this.isOpen) return;

      this.panel.classList.remove('is-visible');
      this.classList.remove('is-open');

      this.trigger.setAttribute(
        'aria-expanded',
        'false'
      );

      document.body.classList.remove(
        'df-store-switcher-open'
      );

      document.removeEventListener(
        'keydown',
        this.handleDocumentKeydown
      );

      this.closeTimer = window.setTimeout(() => {
        this.panel.hidden = true;
        this.backdrop.hidden = true;
      }, 180);

      if (
        restoreFocus &&
        this.lastFocusedElement &&
        typeof this.lastFocusedElement.focus === 'function'
      ) {
        this.lastFocusedElement.focus();
      }
    }

    trapFocus(event) {
      const focusableElements = Array.from(
        this.panel.querySelectorAll(
          [
            'button:not([disabled]):not([tabindex="-1"])',
            'a[href]',
            'input:not([type="hidden"]):not([disabled])',
            'select:not([disabled])',
            'textarea:not([disabled])',
            '[tabindex]:not([tabindex="-1"])'
          ].join(',')
        )
      ).filter((element) => {
        return (
          !element.hidden &&
          element.offsetParent !== null
        );
      });

      if (!focusableElements.length) {
        event.preventDefault();
        this.panel.focus();
        return;
      }

      const firstElement =
        focusableElements[0];

      const lastElement =
        focusableElements[
          focusableElements.length - 1
        ];

      if (
        event.shiftKey &&
        document.activeElement === firstElement
      ) {
        event.preventDefault();
        lastElement.focus();
      } else if (
        !event.shiftKey &&
        document.activeElement === lastElement
      ) {
        event.preventDefault();
        firstElement.focus();
      }
    }
  }

  customElements.define(
    COMPONENT_NAME,
    DressfitStoreSwitcher
  );
})();